<template>
  <div class="outbound-list-component">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Modern Notification Snackbar -->
    <v-snackbar v-model="showsnackbar" color="#9a6b27" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between w-100">
        <span class="text-white font-weight-medium">{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false">
          <v-icon color="#ffffff" size="18">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Unified Stats & Filters Control Bar -->
    <div class="control-panel-card mb-3">
      <!-- Top Row: Compact KPI Stat Tiles -->
      <div class="stats-row d-flex flex-wrap align-center justify-space-between pb-3 mb-3 border-bottom gap-2">
        <div class="stats-strip d-flex flex-wrap align-center gap-2">
          <!-- Gross Weight Stat Tile -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-gross">
              <v-icon size="14" color="#d97706">mdi-scale-balance</v-icon>
            </div>
            <div>
              <div class="stat-label">TOTAL GROSS WT</div>
              <div class="stat-val font-mono">
                {{ formatWeight(totals.totalGrossWeight) }} <span class="stat-unit">gm</span>
              </div>
            </div>
          </div>

          <!-- Net Weight Stat Tile -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-net">
              <v-icon size="14" color="#059669">mdi-gold</v-icon>
            </div>
            <div>
              <div class="stat-label">TOTAL NET WT</div>
              <div class="stat-val font-mono text-net">
                {{ formatWeight(totals.totalNetWeight) }} <span class="stat-unit">gm</span>
              </div>
            </div>
          </div>

          <!-- Valuation Stat Tile -->
          <div class="stat-tile">
            <div class="stat-icon-wrap icon-amount">
              <v-icon size="14" color="#2563eb">mdi-currency-inr</v-icon>
            </div>
            <div>
              <div class="stat-label">PURCHASE VALUATION</div>
              <div class="stat-val font-mono text-amount">
                ₹ {{ formatCurrency(totals.totalAmount) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Select Helper Button -->
        <div class="d-flex align-center gap-2" v-if="List && List.length > 0">
          <button
            type="button"
            class="btn-select-toggle"
            @click="toggleSelectAll(!isAllSelected)"
          >
            <v-icon size="14" color="#64748b" class="mr-1">
              {{ isAllSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
            {{ isAllSelected ? 'Deselect All' : 'Select All Page' }}
          </button>
        </div>
      </div>

      <!-- Bottom Row: Filter Inputs & Primary Batch Button -->
      <div class="d-flex flex-wrap align-center justify-space-between gap-2">
        <!-- Filter Controls -->
        <div class="d-flex flex-wrap align-center gap-2 flex-grow-1">
          <!-- From Date -->
          <div class="filter-box date-box">
            <v-menu
              ref="menuFrom"
              v-model="menuFrom"
              :close-on-content-click="false"
              min-width="auto"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  placeholder="From Date"
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details
                  color="#9a6b27"
                  class="compact-input"
                  prepend-inner-icon="mdi-calendar-start"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                no-title
                scrollable
                color="#9a6b27"
                @change="menuFrom = false"
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- To Date -->
          <div class="filter-box date-box">
            <v-menu
              ref="menuTo"
              v-model="menuTo"
              :close-on-content-click="false"
              min-width="auto"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  placeholder="To Date"
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details
                  color="#9a6b27"
                  class="compact-input"
                  prepend-inner-icon="mdi-calendar-end"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                no-title
                scrollable
                color="#9a6b27"
                @change="menuTo = false"
              ></v-date-picker>
            </v-menu>
          </div>

          <!-- Search Bill No -->
          <div class="filter-box search-box">
            <v-text-field
              v-model="keyword"
              placeholder="Search Bill No..."
              outlined
              dense
              clearable
              hide-details
              color="#9a6b27"
              class="compact-input"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </div>

          <!-- Clear Filters -->
          <v-btn
            v-if="fromDate || toDate || keyword"
            text
            small
            class="btn-reset-filter"
            @click="clearFilters()"
          >
            <v-icon left size="13">mdi-filter-off-outline</v-icon>
            Reset
          </v-btn>
        </div>

        <!-- Primary Action: Create Batch -->
        <div>
          <v-btn
            depressed
            class="btn-create-batch"
            :class="{ 'has-selection': selectedItems.length > 0 }"
            :disabled="selectedItems.length === 0"
            @click="openCreateBatchDialog()"
          >
            <v-icon left size="16">mdi-package-variant-plus</v-icon>
            Create Batch
            <span class="batch-count-pill" v-if="selectedItems.length > 0">
              {{ selectedItems.length }}
            </span>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Selection Banner (Animated when items selected) -->
    <v-expand-transition>
      <div v-if="selectedItems.length > 0" class="selection-banner mb-3">
        <div class="d-flex flex-wrap align-center justify-space-between w-100 gap-2">
          <div class="d-flex align-center">
            <v-icon color="#9a6b27" size="18" class="mr-2">mdi-checkbox-marked-circle</v-icon>
            <span class="selection-text">
              <strong>{{ selectedItems.length }} bill{{ selectedItems.length > 1 ? 's' : '' }}</strong> selected &bull; Net Gold:
              <strong class="text-net">{{ selectedNetWeight.toFixed(3) }} gm</strong> &bull; Value:
              <strong class="text-amount">₹ {{ formatCurrency(selectedAmount) }}</strong>
            </span>
          </div>
          <div class="d-flex align-center gap-2">
            <v-btn text x-small class="btn-clear-selection" @click="clearSelection()">
              Clear Selection
            </v-btn>
            <v-btn depressed small class="btn-proceed-batch" @click="openCreateBatchDialog()">
              Confirm Batch ({{ selectedItems.length }})
            </v-btn>
          </div>
        </div>
      </div>
    </v-expand-transition>

    <!-- Bills Data Table Card -->
    <div class="table-card rounded-lg">
      <div class="table-responsive">
        <table class="custom-table" v-if="List && List.length > 0">
          <thead>
            <tr>
              <th class="col-chk text-center">
                <input
                  type="checkbox"
                  class="custom-chk"
                  :checked="isAllSelected"
                  @change="toggleSelectAll($event.target.checked)"
                  title="Select / Deselect all on this page"
                />
              </th>
              <th class="col-bill text-left">Bill No.</th>
              <th class="col-date text-left">Date</th>
              <th class="col-rate text-right">Gold Rate</th>
              <th class="col-wt text-right">Gross Wt (gm)</th>
              <th class="col-wt text-right">Stone Wt (gm)</th>
              <th class="col-wt text-right">Net Wt (gm)</th>
              <th class="col-melt text-center">Melt (%)</th>
              <th class="col-amount text-right">Total Amt (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(value, i) in List"
              :key="value._id || i"
              :class="{ 'row-selected': isSelected(value._id) }"
              @click="toggleRowSelection(value)"
            >
              <!-- Checkbox -->
              <td class="text-center" @click.stop>
                <input
                  type="checkbox"
                  class="custom-chk"
                  v-model="value.checkbox"
                  @change="selectThis(value.checkbox, value._id, value)"
                />
              </td>

              <!-- Bill No -->
              <td class="text-left">
                <span class="bill-badge">
                  {{ value.billNo || '-' }}
                </span>
              </td>

              <!-- Date -->
              <td class="text-left text-date">
                {{ value.date ? formatDate(value.date) : '-' }}
              </td>

              <!-- Gold Rate -->
              <td class="text-right font-mono text-muted-dark">
                {{ value.goldRate ? '₹ ' + formatRate(value.goldRate) : '-' }}
              </td>

              <!-- Gross Wt -->
              <td class="text-right font-mono">
                {{ formatWeight(value.totalGrossWeight) }}
              </td>

              <!-- Stone Wt -->
              <td class="text-right font-mono text-muted">
                {{ formatWeight(value.totalStoneWeight) }}
              </td>

              <!-- Net Wt -->
              <td class="text-right font-mono text-net-bold">
                {{ formatWeight(value.totalNetWeight) }}
              </td>

              <!-- Melt % -->
              <td class="text-center">
                <span class="melt-pill">
                  {{ value.totalMelt !== undefined && value.totalMelt !== null ? value.totalMelt + '%' : '-' }}
                </span>
              </td>

              <!-- Total Amount -->
              <td class="text-right font-mono amount-bold">
                ₹ {{ formatCurrency(value.totalAmount) }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-else class="empty-state-wrap pa-6 text-center">
          <v-icon size="40" color="#cbd5e1" class="mb-2">mdi-package-variant</v-icon>
          <div class="empty-title">No Old Gold Bills Found</div>
          <div class="empty-desc text-muted mb-3">No pending purchase bills match your current filters.</div>
          <v-btn
            v-if="fromDate || toDate || keyword"
            outlined
            small
            class="btn-action-outline"
            @click="clearFilters()"
          >
            Clear Filters
          </v-btn>
        </div>
      </div>

      <!-- Table Footer with Compact Pagination -->
      <div class="table-footer-bar px-4 py-2 border-top d-flex flex-wrap align-center justify-space-between" v-if="List && List.length > 0">
        <div class="footer-info text-muted">
          Showing <strong>{{ List.length }}</strong> bills {{ totals.count ? `of ${totals.count} total` : '' }}
        </div>
        <div class="footer-pagination">
          <v-pagination
            small
            v-model="page"
            :length="Pagelength || 1"
            color="#9a6b27"
            :total-visible="7"
          ></v-pagination>
        </div>
      </div>
    </div>

    <!-- Create Batch Dialog Modal -->
    <v-dialog width="640px" v-model="createBatchDialog" persistent>
      <v-card class="create-batch-modal rounded-xl">
        <!-- Modal Header -->
        <div class="modal-header d-flex align-center justify-space-between px-5 py-3 border-bottom">
          <div class="d-flex align-center">
            <div class="modal-icon-badge mr-3">
              <v-icon size="18" color="#9a6b27">mdi-package-variant-plus</v-icon>
            </div>
            <div>
              <h2 class="modal-title">Create Acidifying Batch</h2>
              <p class="modal-subtitle mb-0">Group selected bills and dispatch to refiner</p>
            </div>
          </div>
          <v-btn icon small @click="createBatchDialog = false">
            <v-icon color="#64748b" size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Selected Summary Strip -->
        <div class="modal-summary-strip px-5 py-2">
          <div class="d-flex flex-wrap align-center justify-space-between gap-2">
            <div class="summary-chip">
              <span class="chip-label">Bills Count:</span>
              <strong class="chip-val">{{ selectedItems.length }}</strong>
            </div>
            <div class="summary-chip">
              <span class="chip-label">Net Gold:</span>
              <strong class="chip-val text-net">{{ selectedNetWeight.toFixed(3) }} gm</strong>
            </div>
            <div class="summary-chip">
              <span class="chip-label">Total Outlay:</span>
              <strong class="chip-val text-amount">₹ {{ formatCurrency(selectedAmount) }}</strong>
            </div>
          </div>
        </div>

        <!-- Modal Form Body -->
        <div class="modal-body px-5 py-3">
          <v-row dense>
            <!-- Batch Code -->
            <v-col cols="12" sm="6" class="pb-2">
              <label class="form-label">Batch Code <span class="required">*</span></label>
              <v-text-field
                v-model="BatchCode"
                outlined
                dense
                hide-details="auto"
                color="#9a6b27"
                class="compact-input"
                placeholder="e.g. B-101"
              >
                <template v-slot:append>
                  <v-icon
                    small
                    color="#9a6b27"
                    @click="getBatchCode()"
                    title="Regenerate Batch Code"
                    style="cursor: pointer;"
                  >
                    mdi-refresh
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Batch Date -->
            <v-col cols="12" sm="6" class="pb-2">
              <label class="form-label">Batch Date <span class="required">*</span></label>
              <v-menu
                ref="menuBatch"
                v-model="menuBatch"
                :close-on-content-click="false"
                min-width="auto"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="batchDate"
                    outlined
                    dense
                    readonly
                    hide-details="auto"
                    color="#9a6b27"
                    class="compact-input"
                    append-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="batchDate"
                  color="#9a6b27"
                  @change="menuBatch = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Acidifier Select -->
            <v-col cols="12" sm="6" class="pb-2">
              <label class="form-label">Acidifier / Refiner <span class="required">*</span></label>
              <v-autocomplete
                :items="AcidifierList"
                v-model="supplierName"
                placeholder="Select refiner / in-house"
                item-text="supplierName"
                item-value="_id"
                clearable
                outlined
                dense
                hide-details="auto"
                color="#9a6b27"
                class="compact-input"
              ></v-autocomplete>
            </v-col>

            <!-- Remarks -->
            <v-col cols="12" sm="6" class="pb-2">
              <label class="form-label">Remarks / Notes</label>
              <v-text-field
                v-model="remarks"
                outlined
                dense
                hide-details="auto"
                color="#9a6b27"
                class="compact-input"
                placeholder="Optional batch notes..."
              ></v-text-field>
            </v-col>
          </v-row>
        </div>

        <!-- Modal Footer Actions -->
        <div class="modal-footer px-5 py-3 border-top d-flex align-center justify-end gap-2">
          <v-btn
            outlined
            small
            class="btn-action-outline mr-1"
            @click="createBatchDialog = false"
            :disabled="creatingBatch"
          >
            Cancel
          </v-btn>
          <v-btn
            depressed
            small
            class="btn-primary-gold"
            :loading="creatingBatch"
            @click="submitBatch()"
          >
            <v-icon left size="14">mdi-check-circle-outline</v-icon>
            Create Batch ({{ selectedItems.length }})
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "OutboundList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      fromDate: null,
      toDate: null,
      keyword: null,
      menuFrom: false,
      menuTo: false,
      page: 1,
      Pagelength: 1,
      List: [],
      totals: { totalGrossWeight: 0, totalNetWeight: 0, totalAmount: 0, count: 0 },
      selected: [],
      selectedItems: [],
      createBatchDialog: false,
      creatingBatch: false,
      BatchCode: null,
      batchDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      menuBatch: false,
      supplierName: "SELF",
      SupplierList: [],
      remarks: "",
    };
  },
  computed: {
    AcidifierList() {
      return [{ _id: "SELF", supplierName: "Jewellery (Self)" }].concat(
        this.SupplierList || []
      );
    },
    selectedNetWeight() {
      return this.selectedItems.reduce(
        (sum, x) => sum + (Number(x.totalNetWeight) || 0),
        0
      );
    },
    selectedAmount() {
      return this.selectedItems.reduce(
        (sum, x) => sum + (Number(x.totalAmount) || 0),
        0
      );
    },
    isAllSelected() {
      if (!this.List || this.List.length === 0) return false;
      return this.List.every((item) => this.selected.includes(item._id));
    },
  },
  watch: {
    fromDate() {
      this.page = 1;
      this.getList();
    },
    toDate() {
      this.page = 1;
      this.getList();
    },
    keyword() {
      this.page = 1;
      this.getList();
    },
    page() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    getList() {
      this.appLoading = true;
      axios({
        url: "/oldgold/bill/searchlist",
        method: "get",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          from: this.fromDate,
          to: this.toDate,
          keyword: this.keyword,
          page: this.page,
          count: 20,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            const rawList = response.data.oldgoldBill || [];
            this.List = rawList.map((item) => ({
              ...item,
              checkbox: this.selected.includes(item._id),
            }));
            this.totals = response.data.totals || this.totals;
            this.Pagelength = response.data.pages || 1;
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Error fetching old gold bills:", err);
        });
    },
    selectThis(val, id, items1) {
      if (val === true) {
        if (!this.selected.includes(id)) {
          this.selected.push(id);
          this.selectedItems.push(items1);
        }
      } else {
        this.selected = this.selected.filter((x) => x !== id);
        this.selectedItems = this.selectedItems.filter(
          (x) => x._id.toString() !== id.toString()
        );
      }
    },
    toggleRowSelection(item) {
      item.checkbox = !item.checkbox;
      this.selectThis(item.checkbox, item._id, item);
    },
    isSelected(id) {
      return this.selected.includes(id);
    },
    toggleSelectAll(val) {
      if (!this.List) return;
      this.List.forEach((item) => {
        item.checkbox = val;
        this.selectThis(val, item._id, item);
      });
    },
    clearSelection() {
      this.selected = [];
      this.selectedItems = [];
      if (this.List) {
        this.List.forEach((item) => {
          item.checkbox = false;
        });
      }
    },
    clearFilters() {
      this.fromDate = null;
      this.toDate = null;
      this.keyword = null;
      this.page = 1;
      this.getList();
    },
    openCreateBatchDialog() {
      if (this.selectedItems.length === 0) {
        this.msg = "Please select at least one old gold bill to batch";
        this.showsnackbar = true;
        return;
      }
      this.getBatchCode();
      this.getSupplier();
      this.createBatchDialog = true;
    },
    getBatchCode() {
      axios({
        url: "/batchcode/generate",
        method: "POST",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.BatchCode = response.data.batchno;
          }
        })
        .catch((err) => {
          console.error("Error generating batch code:", err);
        });
    },
    getSupplier() {
      axios({
        method: "get",
        url: "/supplier/getlist",
        headers: { "x-auth-token": localStorage.getItem("token") },
      })
        .then((response) => {
          if (response.data.status === true) {
            this.SupplierList = response.data.data || [];
          }
        })
        .catch((err) => {
          console.error("Error fetching suppliers:", err);
        });
    },
    submitBatch() {
      if (!this.BatchCode) {
        this.msg = "Please provide batch code";
        this.showsnackbar = true;
        return;
      }
      if (!this.batchDate) {
        this.msg = "Please choose batch date";
        this.showsnackbar = true;
        return;
      }
      this.creatingBatch = true;
      axios({
        url: "/OldGoldBatch/add",
        method: "POST",
        headers: { "x-auth-token": localStorage.getItem("token") },
        data: {
          oldGoldBills: this.selectedItems,
          batchNumber: this.BatchCode,
          batchDate: this.batchDate,
          acidifier: this.supplierName === "SELF" ? null : this.supplierName,
          remarks: this.remarks,
        },
      })
        .then((response) => {
          this.creatingBatch = false;
          if (response.data.status === true) {
            this.msg = "Batch created successfully!";
            this.showsnackbar = true;
            this.createBatchDialog = false;
            this.remarks = "";
            this.supplierName = "SELF";
            this.clearSelection();
            this.getList();
            this.$emit("batch-created");
          } else if (response.data.isExist === true) {
            this.msg = "Batch code already exists. Generated a new one.";
            this.showsnackbar = true;
            this.getBatchCode();
          } else {
            this.msg = response.data.msg || "Failed to create batch";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.creatingBatch = false;
          this.ServerError = true;
          console.error("Error creating batch:", err);
        });
    },
    formatDate(item) {
      if (!item) return "-";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return item;
      const day = dt.getDate();
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
      ];
      return `${day} ${monthNames[dt.getMonth()]} ${dt.getFullYear()}`;
    },
    formatCurrency(val) {
      if (val === undefined || val === null || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatRate(val) {
      if (val === undefined || val === null || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN");
    },
    formatWeight(val) {
      if (val === undefined || val === null || isNaN(val)) return "0.000";
      return Number(val).toFixed(3);
    },
  },
};
</script>

<style scoped>
.outbound-list-component {
  font-family: "Plus Jakarta Sans", "Inter", sans-serif;
  color: #0f172a;
}

/* Utilities */
.gap-2 { gap: 8px; }
.w-100 { width: 100%; }

/* Unified Control Panel Card */
.control-panel-card {
  background: #ffffff;
  border: 1px solid #e8e4dc;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

/* Stat Tiles */
.stat-tile {
  display: inline-flex;
  align-items: center;
  background: #fdfcfb;
  border: 1px solid #eee9e0;
  border-radius: 8px;
  padding: 6px 12px;
}

.stat-icon-wrap {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.icon-gross { background: #fffbeb; }
.icon-net { background: #ecfdf5; }
.icon-amount { background: #eff6ff; }

.stat-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #64748b;
  text-transform: uppercase;
  line-height: 1.1;
}

.stat-val {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.stat-unit {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.text-net { color: #047857; }
.text-amount { color: #1d4ed8; }

/* Filter Inputs */
.date-box {
  width: 140px;
}

.search-box {
  width: 180px;
}

.compact-input >>> .v-input__slot {
  min-height: 34px !important;
  height: 34px !important;
  font-size: 12.5px !important;
  border-radius: 7px !important;
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
  padding: 0 8px !important;
}

.compact-input >>> fieldset {
  border-color: #e2e8f0 !important;
}

.compact-input:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

.compact-input.v-input--is-focused >>> fieldset {
  border-color: #9a6b27 !important;
}

.compact-input >>> .v-input__prepend-inner {
  margin-top: 6px !important;
  margin-right: 4px !important;
}

.compact-input >>> .v-icon {
  font-size: 15px !important;
  color: #94a3b8 !important;
}

.compact-input >>> input {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
}

.btn-select-toggle {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-select-toggle:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.btn-reset-filter {
  color: #64748b !important;
  text-transform: none !important;
  font-size: 11.5px !important;
}

/* Primary Batch Button */
.btn-create-batch {
  height: 34px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 2px 4px rgba(154, 106, 44, 0.2) !important;
}

.btn-create-batch:disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
}

.btn-create-batch.has-selection {
  box-shadow: 0 3px 8px rgba(154, 106, 44, 0.35) !important;
}

.batch-count-pill {
  background: #ffffff;
  color: #875c24;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 999px;
  margin-left: 6px;
}

/* Selection Banner */
.selection-banner {
  background: #fdfaf6;
  border: 1px solid #ebd9be;
  border-radius: 8px;
  padding: 8px 14px;
}

.selection-text {
  font-size: 12.5px;
  color: #0f172a;
}

.btn-clear-selection {
  font-size: 11px !important;
  color: #64748b !important;
  text-transform: none !important;
}

.btn-proceed-batch {
  height: 28px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 5px !important;
}

/* Table Card */
.table-card {
  background-color: #ffffff;
  border: 1px solid #e8e4dc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.border-top { border-top: 1px solid #f1f5f9; }

.custom-chk {
  width: 16px;
  height: 16px;
  accent-color: #9a6b27;
  cursor: pointer;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.custom-table thead {
  background-color: #fafaf9;
}

.custom-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  border-bottom: 1px solid #eef1f5;
  user-select: none;
}

.custom-table td {
  padding: 10px 14px;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
}

.custom-table tbody tr {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.custom-table tbody tr:hover td {
  background-color: #faf8f5;
}

.custom-table tbody tr.row-selected td {
  background-color: #fdfbf7;
}

.custom-table tbody tr.row-selected td:first-child {
  border-left: 3px solid #9a6b27;
}

.bill-badge {
  display: inline-block;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 2px 7px;
  border-radius: 5px;
}

.text-date {
  color: #475569;
  font-size: 12.5px;
  white-space: nowrap;
}

.font-mono {
  font-variant-numeric: tabular-nums;
}

.text-muted-dark { color: #475569; }
.text-muted { color: #94a3b8; }
.text-net-bold { color: #047857; font-weight: 700; }
.amount-bold { color: #0f172a; font-weight: 700; }

.melt-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  padding: 1px 7px;
  border-radius: 999px;
}

.empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.empty-desc {
  font-size: 12px;
}

.footer-info {
  font-size: 12px;
}

/* Modal */
.create-batch-modal {
  background: #ffffff;
  overflow: hidden;
}

.modal-icon-badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fbf5ec;
  border: 1px solid #ebd9be;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.modal-summary-strip {
  background: #fafaf9;
  border-bottom: 1px solid #f1f5f9;
}

.summary-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #475569;
}

.chip-label { margin-right: 5px; }

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.required { color: #ef4444; }

.btn-primary-gold {
  height: 34px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 14px !important;
}

.btn-action-outline {
  height: 34px !important;
  border: 1px solid #e2e8f0 !important;
  color: #475569 !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 12px !important;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
}
</style>
